import { useCallback, useEffect, useRef, useState } from 'react';
import {
    useEditEmpresaStateMutation,
    useGetEstablishmentDetailQuery,
    useLazyGetEstablishmentDetailQuery
} from '../../../services/companyApi';
import { useAppDispatch } from '../../../hooks/reduxHooks';
import {
    useGetDatosParametricosQuery,
    useGetTiposPresentacionesQuery,
    useLazyGetPresentacionesByEstablecimientoQuery,
    useDownloadPresentationsRARMutation,
    useLazyGetPresentationRarFileStatusQuery,
    useUpdateMassivePresentationsStatesMutation,
    useExportMassivePresentationsByIdsAsyncMutation,
    useLazyGetMassivePresentationsByIdsFileStatusQuery,
    useExportRGRLPresentacionesMutation
} from '../../../services/presentacionesApi';
import {
    ESTABLISHMENT_PRESENTATION_TABS,
    ESTABLISHMENT_PRESENTATION_TAB_QUERY_PARAMS,
    isContratoInactivo,
    PRESENTATION_STATE,
    PRESENTATION_STATE_UPDATE_MAP,
    PRESENTATION_STATE_UPDATE_TYPES,
    PRESENTATION_TYPE
} from '../consts';
import { setParametricData } from '../../../redux/slices/datosParametricosSlice';
import { enqueueSnackbar } from 'notistack';
import { showAlert } from '../../../features/slices/applicationSlice';
import { ALERT_CONFIRM_TEXT, ERROR_CONTACT_SUPPORT } from '../../../constants/values';
import { useTranslation } from 'react-i18next';
import { downloadFile, getCurrentDate, handleErrors } from '../../../utils/common';
import {
    EstablishmentActivityType,
    selectActivityTypeById
} from '../../../redux/slices/establishmentSlice';
import { useSelector } from 'react-redux';
import useItemsSelection from '../../../hooks/useItemsSelection';
import usePresentationsBOTableTabs from './usePresentationsBOTableTabs';
import useDrawerManager from '../../../hooks/useDrawerManager';
import {
    useSendCarcinogenicSRTMutation,
    useNotificarCorreccionCargadaMutation
} from '../../../services/carcinogenicApi';

export interface tipo_presentaciones {
    id: number;
    nombre: string;
    detalle: string;
}
export interface rowPresentation {
    id: number;
    estado: PRESENTATION_STATE;
    tipo?: tipo_presentaciones;
    idEstado?: PRESENTATION_STATE;
}

// Estado SVCC (cliente) resuelto por informe puntual — sólo aplica a filas
// de tipo Cancerígenos (PRESENTATION_TYPE.CANCERIGENOS). Se guarda por
// idInforme porque el endpoint es liviano y scoped a un informe, no al CUIT
// completo como en la grilla de backoffice.
export interface EstadoSvccCliente {
    estado: string;
    motivoRechazo: string | null;
    // Resuelto por el backend (estado-svcc-cliente) — necesario en el Front
    // para poder disparar acciones scoped a CUIT (p. ej. notificar
    // correccion) sin duplicar la resolucion CUIT<->informe acá.
    cuit?: string | null;
    /**
     * Errores bloqueantes que el informe tiene HOY según la validación local.
     * 0 = ya se puede notificar la corrección; > 0 = el botón queda cerrado.
     * undefined/null (backend anterior, o validación caída) no bloquea.
     */
    observacionesPendientes?: number | null;
}
const usePresentations = ({ extraFilters, filters, establishmentId, clearFilters }: any) => {
    const dispatch = useAppDispatch();
    const [currentEstablishmentTab, setCurrentEstablishmentTab] = useState(
        ESTABLISHMENT_PRESENTATION_TABS.ACTIVE
    );
    const [selected, setSelected] = useState<rowPresentation | null>(null);
    const [selectedItemFromAction, setSelectedItemFromAction] = useState<any>(null);
    const [estadoSvccByInforme, setEstadoSvccByInforme] = useState<
        Record<number, EstadoSvccCliente>
    >({});
    const [notificandoCorreccionInforme, setNotificandoCorreccionInforme] = useState<
        number | null
    >(null);
    /*
     * El informe cuya notificación de corrección está esperando confirmación.
     * El botón del banner ya no dispara el mail: abre el diálogo, y recién
     * "Notificar" llama al endpoint. Un click de más avisaba al backoffice que
     * la corrección estaba lista cuando no lo estaba.
     */
    const [correccionAConfirmar, setCorreccionAConfirmar] = useState<{
        idInforme: number;
        cuit: string;
    } | null>(null);
    const [openCard, setOpenCard] = useState<boolean>(false);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [menuOpen, setMenuOpen] = useState(false);
    const [menuOpenAdmin, setMenuOpenAdmin] = useState(false);
    const [isLoadingRARsFile, setIsLoadingRARsFile] = useState(false);
    const [isLoadingMassiveExportByIds, setIsLoadingMassiveExportByIds] = useState(false);
    const [editState, { isLoading: edittingPresentationState }] = useEditEmpresaStateMutation();
    const [downloadRarsFile] = useDownloadPresentationsRARMutation();
    const [getPresentationRarFileStatus] = useLazyGetPresentationRarFileStatusQuery();
    const [exportMassivePresentationsByIdsAsync] =
        useExportMassivePresentationsByIdsAsyncMutation();
    const [getMassivePresentationsByIdsFileStatus] =
        useLazyGetMassivePresentationsByIdsFileStatusQuery();

    const [updateMassivePresentationsStates, { isLoading: isUpdatingMassivePresentationsStates }] =
        useUpdateMassivePresentationsStatesMutation();

    const [sendCarcinogenicReportToSRT, { isLoading: isSendingCarcinogenicReportToSRT }] =
        useSendCarcinogenicSRTMutation();

    const [exportRGRLPresentaciones, { isLoading: isExportingRGRL }] =
        useExportRGRLPresentacionesMutation();

    const [notificarCorreccionCargadaMutation] = useNotificarCorreccionCargadaMutation();

    const presentationsSelection = useItemsSelection<rowPresentation>();
    const previousOrganizationIdRef = useRef<any>(undefined);

    const presentationsDrawerManager = useDrawerManager();

    const {
        data: tiposPresentaciones,
        error: tiposPresentacionesError,
        isLoading: loadingPresentationtypes
    } = useGetTiposPresentacionesQuery(establishmentId);

    const presentationsTabs = usePresentationsBOTableTabs({
        filters: filters,
        extraFilters: extraFilters,
        clearFilters,
        clearAllSelected: presentationsSelection.clearAllSelected
    });

    const { t } = useTranslation('presentaciones');

    const { data: parametricData, isLoading: isLoadingParametricos } =
        useGetDatosParametricosQuery();

    const {
        data: establishmentDetail,
        isLoading: isLoadingEstablishmentDetail,
        isFetching: isFetchingEstablishmentDetail
        // En backoffice la pantalla arranca sin establecimiento elegido, así que
        // sin el skip la query salía como `GET /establecimientos/undefined` en
        // cada carga: 404 seguro y ruido en la consola. El resto de las queries
        // del hook ya cortaban con la misma condición.
    } = useGetEstablishmentDetailQuery(
        { establishmentId },
        { skip: !establishmentId || establishmentId <= 0 }
    );

    const establishmentPresentationRGRLType: EstablishmentActivityType | null = useSelector(
        selectActivityTypeById(establishmentDetail?.tipoActividad?.id)
    );

    const [
        getPresentationsByEstablishment,
        {
            data: presentationsData,
            error: presentationsDataError,
            isLoading: isLoadingPresentationsData,
            isFetching: isFetchingPresentationsData
        }
    ] = useLazyGetPresentacionesByEstablecimientoQuery({
        ...filters,
        idEstablecimiento: establishmentId
    });

    const currentTabQueryParams =
        ESTABLISHMENT_PRESENTATION_TAB_QUERY_PARAMS[currentEstablishmentTab];

    const getTabScopedFilters = (sourceFilters: Record<string, any> | null = {}) => {
        const scopedFilters =
            sourceFilters && typeof sourceFilters === 'object' ? sourceFilters : {};

        if (currentEstablishmentTab === ESTABLISHMENT_PRESENTATION_TABS.HISTORY) {
            return scopedFilters;
        }

        const {
            idTipo,
            fechaDesde,
            fechaHasta,
            fechaPresentacionDesde,
            fechaPresentacionHasta,
            ...rest
        } = scopedFilters;
        return rest;
    };

    useEffect(() => {
        if (!isLoadingParametricos && parametricData) {
            dispatch(setParametricData(parametricData.data));
        }
    }, [isLoadingParametricos, parametricData, dispatch]);

    const handleOpenCard = (item: any) => {
        setOpenCard(true);
        setSelected(item);
    };

    const handleCloseCard = () => {
        setOpenCard(false);
        clearSelected();
    };

    useEffect(() => {
        const combinedFilters = {
            ...getTabScopedFilters(filters),
            ...getTabScopedFilters(extraFilters),
            ...currentTabQueryParams
        };

        if (openCard) handleCloseCard();

        if (!establishmentId || establishmentId <= 0) return;

        getPresentationsByEstablishment({
            ...combinedFilters,
            idEstablecimiento: establishmentId
        });
        // eslint-disable-next-line
    }, [filters, extraFilters, establishmentId, currentTabQueryParams]);

    const refetchPresentations = () => {
        if (!establishmentId || establishmentId <= 0) {
            return;
        }
        getPresentationsByEstablishment({
            ...getTabScopedFilters(filters),
            ...getTabScopedFilters(extraFilters),
            ...currentTabQueryParams,
            idEstablecimiento: establishmentId
        });
    };

    const handleEstablishmentTabChange = (_event: React.SyntheticEvent, newValue: number) => {
        setCurrentEstablishmentTab(newValue);
    };

    const checkIfCarcinogenicRarPresentationIsAvailable = async () => {
        try {
            const rarPresentations: any = await getPresentationsByEstablishment({
                idEstablecimiento: establishmentId,
                skip: 0,
                take: 1000,
                idTipo: PRESENTATION_TYPE.RAR,
                idEstado: PRESENTATION_STATE.RECIBIDO
            });

            if (!rarPresentations?.data?.data?.data?.length) return false;

            const rarPresentation = rarPresentations?.data?.data?.data?.find(
                (presentation: any) =>
                    presentation?.idEstado === PRESENTATION_STATE.RECIBIDO ||
                    presentation?.idEstado === PRESENTATION_STATE.PRESENTADO ||
                    presentation?.idEstado === PRESENTATION_STATE.EN_REVISION ||
                    presentation?.idEstado === PRESENTATION_STATE.VISADO
            );

            return !!rarPresentation;
        } catch (error) {
            dispatch(
                showAlert({
                    title: 'No se pudo verificar la disponibilidad de presentaciones',
                    message: ERROR_CONTACT_SUPPORT,
                    icon: 'error',
                    confirmText: ALERT_CONFIRM_TEXT
                })
            );
            return false;
        }
    };

    const clearSelected = () => {
        setSelected(null);
        setSelectedItemFromAction(null);
    };

    // Guarda el resultado por idInforme. Evita setState cuando el valor no
    // cambia — sin esto, el efecto del componente que reporta el resultado
    // (uno por fila SVCC visible) volvería a dispararse en cada render del
    // padre y generaría un loop de renders.
    const registrarEstadoSvcc = useCallback(
        (idInforme: number, resultado: EstadoSvccCliente) => {
            setEstadoSvccByInforme((prev) => {
                const anterior = prev[idInforme];
                // También las observaciones: cambian cuando el cliente guarda el
                // informe, y son lo que abre o cierra "Notificar corrección".
                if (
                    anterior?.estado === resultado?.estado &&
                    anterior?.motivoRechazo === resultado?.motivoRechazo &&
                    anterior?.observacionesPendientes === resultado?.observacionesPendientes
                ) {
                    return prev;
                }
                return { ...prev, [idInforme]: resultado };
            });
        },
        []
    );

    const handleClickAction = (item: any, event: any) => {
        setSelected(item);
        setAnchorEl(event.currentTarget);
        setMenuOpen(true);
    };

    const handleClickActionAdmin = (item: any, event: any) => {
        setSelected(item);
        setAnchorEl(event.currentTarget);
        setMenuOpenAdmin(true);
    };

    const handleCloseMenu = () => {
        setMenuOpen(false);
        setSelectedItemFromAction(null);
    };
    const handleCloseMenuAdmin = () => {
        setMenuOpenAdmin(false);
        setSelectedItemFromAction(null);
    };

    const MAX_RETRIES = 20;
    const POLLING_INTERVAL_MS = 3000;

    const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

    const sanitizeFileNamePart = (value: string) =>
        value
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/[^a-zA-Z0-9 _-]/g, '')
            .trim()
            .replace(/\s+/g, '_');

    const getClientNameFromFilters = (currentFilters: any, currentExtraFilters: any): string | null => {
        const rawName =
            currentExtraFilters?.idOrganizacion?.nombre ||
            currentExtraFilters?.idOrganizacion?.label ||
            currentFilters?.idOrganizacion?.nombre ||
            currentFilters?.idOrganizacion?.label ||
            currentExtraFilters?.idOrganizacion?.value ||
            currentFilters?.idOrganizacion?.value ||
            null;

        if (!rawName || typeof rawName !== 'string') return null;

        const sanitized = sanitizeFileNamePart(rawName);
        return sanitized || null;
    };

    const getOrganizationId = (organizationFilter: any) => {
        if (!organizationFilter) return null;
        if (typeof organizationFilter === 'object') {
            return organizationFilter?.id ?? organizationFilter?.value ?? null;
        }

        return organizationFilter;
    };

    useEffect(() => {
        const currentOrganizationId =
            getOrganizationId(extraFilters?.idOrganizacion) ??
            getOrganizationId(filters?.idOrganizacion) ??
            null;

        if (
            previousOrganizationIdRef.current !== undefined &&
            previousOrganizationIdRef.current !== currentOrganizationId
        ) {
            presentationsSelection.clearAllSelected();
        }

        previousOrganizationIdRef.current = currentOrganizationId;
    }, [extraFilters?.idOrganizacion, filters?.idOrganizacion, presentationsSelection]);

    const normalizeDownloadFilters = (currentFilters: any, currentExtraFilters: any) => {
        const merged = {
            ...(currentFilters || {}),
            ...(currentExtraFilters || {})
        };

        const { take, skip, fechaFin, fechaInicio, fechaDesde, fechaHasta, ...rawFilters } = merged;

        const result = Object.entries(rawFilters).reduce((acc: any, [key, value]) => {
            if (value === undefined || value === null || value === '') return acc;

            if (typeof value === 'object') {
                let normalizedValue: any = null;

                if (key === 'estado') {
                    normalizedValue = (value as any)?.value ?? null;
                } else if (key.toLowerCase().includes('id')) {
                    normalizedValue = (value as any)?.id ?? (value as any)?.value ?? null;
                } else {
                    normalizedValue =
                        (value as any)?.value ??
                        (value as any)?.id ??
                        (value as any)?.nombre ??
                        null;
                }

                if (normalizedValue !== null && normalizedValue !== '') {
                    acc[key] = normalizedValue;
                }

                return acc;
            }

            acc[key] = value;
            return acc;
        }, {});

        if (fechaDesde) result.fechaDesde = fechaDesde;
        if (fechaHasta) result.fechaHasta = fechaHasta;

        return result;
    };

    const triggerDownloadFromUrl = async (url: string, clientName?: string | null) => {
        const response = await fetch(url);

        if (!response.ok) {
            throw new Error('Error downloading RAR file');
        }

        const blob = await response.blob();
        const now = new Date();
        const hours = String(now.getHours()).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');
        const clientSuffix = clientName ? `_${clientName}` : '';
        const fileName = `Presentaciones-RAR${clientSuffix}_${getCurrentDate()}_${hours}-${minutes}.xlsx`;

        downloadFile({ blob, name: fileName });
    };

    const handleDownloadRARsFile = async () => {
        try {
            const selectedClientId =
                extraFilters?.idOrganizacion?.id ??
                extraFilters?.idOrganizacion ??
                filters?.idOrganizacion ??
                null;

            if (!selectedClientId) {
                enqueueSnackbar(
                    t('massiveActions.noClientSelected'),
                    { variant: 'warning' }
                );
                return;
            }

            setIsLoadingRARsFile(true);

            const normalizedFilters = normalizeDownloadFilters(filters, extraFilters);
            const clientName = getClientNameFromFilters(filters, extraFilters);
            const createJobResponse: any = await downloadRarsFile(normalizedFilters).unwrap();
            const parsedPayload =
                typeof createJobResponse === 'string'
                    ? JSON.parse(createJobResponse)
                    : createJobResponse;
            const jobId = parsedPayload?.jobId;
            if (!jobId) {
                enqueueSnackbar('No se pudo obtener el job ID para la exportación.', {
                    variant: 'error'
                });
                return;
            }

            for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
                try {
                    const statusResponse: any = await getPresentationRarFileStatus(jobId).unwrap();
                    const fileUrl = statusResponse?.url;

                    if (fileUrl) {
                        await triggerDownloadFromUrl(fileUrl, clientName);
                        enqueueSnackbar(t('Excel generado con éxito'), { variant: 'success' });
                        return;
                    }
                } catch (error: any) {
                    const status = error?.status ?? error?.originalStatus;

                    if (status !== 404) {
                        dispatch(
                            showAlert({
                                title: 'No se pudo exportar el archivo',
                                message: ERROR_CONTACT_SUPPORT,
                                icon: 'error',
                                confirmText: ALERT_CONFIRM_TEXT
                            })
                        );
                        return;
                    }
                }

                await wait(POLLING_INTERVAL_MS);
            }

            enqueueSnackbar(t('Algo salió mal...'), { variant: 'error' });
            return;
        } catch (error: any) {
            if (error?.status || error?.data) {
                handleErrors(error);
                return;
            }

            dispatch(
                showAlert({
                    title: 'No se pudo descargar el archivo de RARs',
                    message: ERROR_CONTACT_SUPPORT,
                    icon: 'error',
                    confirmText: ALERT_CONFIRM_TEXT
                })
            );
        } finally {
            setIsLoadingRARsFile(false);
        }
    };

    const handleMassiveStateChangeClick = async (
        newState: keyof typeof PRESENTATION_STATE_UPDATE_TYPES
    ) => {
        try {
            const stateId = PRESENTATION_STATE_UPDATE_MAP[newState];

            if (!stateId) return;
            const filteredPresentations = presentationsSelection.currentSelectedItems.filter(
                (presentation: any) =>
                    (presentation?.estado !== PRESENTATION_STATE.PRESENTADO ||
                        presentation?.idEstado !== PRESENTATION_STATE.PRESENTADO) &&
                    presentation?.idEstado !== PRESENTATION_STATE.INACTIVO &&
                    !isContratoInactivo(presentation)
            );
            if(filteredPresentations.length === 0) {
                enqueueSnackbar(t('massiveActions.stateChange.somePresentationsCantChangeState'), { variant: 'info' });
                return;
            }

            if (
                filteredPresentations.length !==
                presentationsSelection.currentSelectedItems.length
            ) {
                enqueueSnackbar(t('massiveActions.stateChange.somePresentationsWontChangeState'), {
                    variant: 'info'
                });
            }

            const presentationIds = filteredPresentations.map(
                (presentation: any) => presentation.id
            );
            const response: any = await updateMassivePresentationsStates({
                idEstado: stateId,
                ids: presentationIds
            });
            if (response?.error) {
                handleErrors(response.error);
                return;
            }
            enqueueSnackbar(t('massiveActions.stateChange.success'), { variant: 'success' });
            presentationsSelection.clearAllSelected();
        } catch (error) {
            dispatch(
                showAlert({
                    title: 'No se pudo actualizar el estado masivo de presentaciones',
                    message: ERROR_CONTACT_SUPPORT,
                    icon: 'error',
                    confirmText: ALERT_CONFIRM_TEXT
                })
            );
        }
    };
    
    const handleMassiveExportClick = async () => {
        try {
            const selectedIds = presentationsSelection.currentSelectedItems.map(
                (presentation: any) => presentation.id
            );

            if (!selectedIds.length) {
                enqueueSnackbar(t('Algo salió mal...'), { variant: 'warning' });
                return;
            }

            const organizationIdFromFilters = getOrganizationId(filters?.idOrganizacion);
            const organizationIdFromExtraFilters = getOrganizationId(extraFilters?.idOrganizacion);

            if (
                !organizationIdFromFilters ||
                !organizationIdFromExtraFilters ||
                String(organizationIdFromFilters) !== String(organizationIdFromExtraFilters)
            ) {
                enqueueSnackbar(t('massiveActions.noClientSelected'), { variant: 'warning' });
                return;
            }

            setIsLoadingMassiveExportByIds(true);

            const createJobResponse: any = await exportMassivePresentationsByIdsAsync({
                idsPresentaciones: selectedIds
            }).unwrap();
            const parsedPayload =
                typeof createJobResponse === 'string'
                    ? JSON.parse(createJobResponse)
                    : createJobResponse;
            const jobId = parsedPayload?.jobId;

            if (!jobId) {
                enqueueSnackbar('No se pudo obtener el job ID para la exportación.', {
                    variant: 'error'
                });
                return;
            }

            const clientName = getClientNameFromFilters(filters, extraFilters);

            for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
                try {
                    const statusResponse: any =
                        await getMassivePresentationsByIdsFileStatus(jobId).unwrap();
                    const fileUrl = statusResponse?.url;

                    if (fileUrl) {
                        await triggerDownloadFromUrl(fileUrl, clientName);
                        enqueueSnackbar(t('massiveActions.export.success'), { variant: 'success' });
                        presentationsSelection.clearAllSelected();
                        return;
                    }
                } catch (error: any) {
                    const status = error?.status ?? error?.originalStatus;

                    if (status !== 404) {
                        dispatch(
                            showAlert({
                                title: 'No se pudo exportar el archivo',
                                message: ERROR_CONTACT_SUPPORT,
                                icon: 'error',
                                confirmText: ALERT_CONFIRM_TEXT
                            })
                        );
                        return;
                    }
                }

                await wait(POLLING_INTERVAL_MS);
            }

            enqueueSnackbar(t('Algo salió mal...'), { variant: 'error' });
        } catch (error: any) {
            if (error?.status || error?.data) {
                handleErrors(error);
                return;
            }

            dispatch(
                showAlert({
                    title: 'No se pudo exportar las presentaciones',
                    message: ERROR_CONTACT_SUPPORT,
                    icon: 'error',
                    confirmText: ALERT_CONFIRM_TEXT
                })
            );
        } finally {
            setIsLoadingMassiveExportByIds(false);
        }
    };

    const handleSendCarcinogenicReportToSRT = async () => {
        try {
            if (!selected?.id) return;

            await sendCarcinogenicReportToSRT(selected.id).unwrap();
            enqueueSnackbar(t('SRT enviada a SRT con éxito'), { variant: 'success' });
            refetchPresentations();
        } catch (error: any) {
            // Este endpoint (POST /:idInforme/vincular) rutea al mismo use case
            // que el batch de SVCCVincularDrawer: ante un hallazgo bloqueante
            // devuelve 400 con `BadRequestException({ bloqueados })`, que no
            // trae `message` propio. Nest completa `error.data.message` con el
            // nombre de la clase ("Bad Request Exception"), que no le sirve al
            // usuario. Por eso se corta aquí con los mensajes reales de los
            // hallazgos en vez de caer a `handleErrors`, que mostraría ese
            // texto crudo.
            const detalle = error?.data?.bloqueados;
            if (Array.isArray(detalle) && detalle.length) {
                const nombreEstablecimiento = establishmentDetail?.nombre;
                const mensajes = detalle.flatMap((bloqueado: any) =>
                    Array.isArray(bloqueado?.errores)
                        ? bloqueado.errores
                              .map((hallazgo: any) => hallazgo?.mensajeUsuario)
                              .filter(Boolean)
                        : []
                );

                (mensajes.length
                    ? mensajes
                    : ['Hay datos a corregir en el establecimiento.']
                ).forEach((mensaje: string) => {
                    enqueueSnackbar(
                        nombreEstablecimiento ? `${nombreEstablecimiento}: ${mensaje}` : mensaje,
                        { variant: 'error' }
                    );
                });
                return;
            }
            handleErrors(error);
        }
    };

    // Notifica al backoffice que el cliente ya cargó la corrección de un
    // informe SVCC RECHAZADA. El endpoint opera por CUIT (no por idInforme),
    // por eso recibe el CUIT ya resuelto desde estadoSvccByInforme (Task 8/9)
    // en vez de volver a resolverlo acá.
    const handleNotificarCorreccionCargada = async (idInforme: number, cuit: string) => {
        setNotificandoCorreccionInforme(idInforme);
        try {
            await notificarCorreccionCargadaMutation(cuit).unwrap();
            enqueueSnackbar('Corrección notificada correctamente.', { variant: 'success' });
        } catch (error) {
            handleErrors(error);
        } finally {
            setNotificandoCorreccionInforme(null);
            setCorreccionAConfirmar(null);
        }
    };

    const pedirConfirmacionNotificarCorreccion = (idInforme: number, cuit: string) =>
        setCorreccionAConfirmar({ idInforme, cuit });

    const cancelarNotificarCorreccion = () => setCorreccionAConfirmar(null);

    const confirmarNotificarCorreccion = () => {
        if (!correccionAConfirmar) return;
        handleNotificarCorreccionCargada(correccionAConfirmar.idInforme, correccionAConfirmar.cuit);
    };

    const handleExportRGRLPresentaciones = async () => {
        try {
            const selectedClientId =
                extraFilters?.idOrganizacion?.id ??
                extraFilters?.idOrganizacion ??
                filters?.idOrganizacion ??
                null;

            if (!selectedClientId) {
                enqueueSnackbar(t('massiveActions.noClientSelected'), { variant: 'warning' });
                return;
            }

            const normalizedFilters = normalizeDownloadFilters(filters, extraFilters);
            const { idTipo, ...otherFilters } = normalizedFilters;

            const body = idTipo
                ? { ...otherFilters, idTipo }
                : { ...otherFilters, allRGRLs: true };

            const base64 = await exportRGRLPresentaciones(body).unwrap();
            if (!base64) return;

            const byteCharacters = atob(base64);
            const byteArray = new Uint8Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteArray[i] = byteCharacters.charCodeAt(i);
            }
            const blob = new Blob([byteArray], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });
            downloadFile({ blob, name: `Presentaciones_RGRL_${getCurrentDate()}.xlsx` });
        } catch (error) {
            handleErrors(error);
        }
    };

    return {
        parametricData,
        selected,
        currentEstablishmentTab,
        openCard,
        selectedItemFromAction,
        anchorEl,
        menuOpen,
        menuOpenAdmin,
        presentationsData,
        presentationsDataError,
        isLoadingPresentationsData,
        isFetchingPresentationsData,
        establishmentDetail,
        isLoadingEstablishmentDetail,
        tiposPresentaciones,
        tiposPresentacionesError,
        loadingPresentationtypes,
        edittingPresentationState,
        isLoadingRARsFile,
        establishmentPresentationRGRLType,
        checkIfCarcinogenicRarPresentationIsAvailable,
        setAnchorEl,
        refetchPresentations,
        getPresentationsByEstablishment,
        editState,
        setSelectedItemFromAction,
        setOpenCard,
        handleClickAction,
        handleClickActionAdmin,
        handleEstablishmentTabChange,
        handleOpenCard,
        setSelected,
        handleCloseMenu,
        handleCloseMenuAdmin,
        handleCloseCard,
        handleDownloadRARsFile,
        presentationsSelection,
        presentationsTabs,
        handleMassiveStateChangeClick,
        handleMassiveExportClick,
        isUpdatingMassivePresentationsStates,
        isLoadingMassiveExportByIds,
        presentationsDrawerManager,
        handleSendCarcinogenicReportToSRT,
        isSendingCarcinogenicReportToSRT,
        handleExportRGRLPresentaciones,
        isExportingRGRL,
        estadoSvccByInforme,
        registrarEstadoSvcc,
        notificandoCorreccionInforme,
        handleNotificarCorreccionCargada,
        correccionAConfirmar,
        pedirConfirmacionNotificarCorreccion,
        confirmarNotificarCorreccion,
        cancelarNotificarCorreccion
    };
};
export default usePresentations;
